import { z } from 'zod/v3';
import { ToolModel } from './tool.model';
import { GraphModel } from '../graph/graph.model';
import { err, ok } from 'neverthrow';
import {
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '../actions/framework';
import { Assert } from '@cotera/utilities';
import { ActionResult, AskResponse } from '../actions/framework/types';
import { useState } from 'react';
import { toast, Loading } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { ModalPageView } from '@cotera/client/app/components/app';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { DG } from '@cotera/sdk/core';
import { QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';

type Params = {
  connectionData: {
    type: 'apiKey' | 'oauth' | 'password' | 'noAuth';
    provider: string;
    displayName: string;
    secret?: any;
  };
  updateScope: 'single' | 'all';
};

export class UpdateCredentialsAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Update Connection';
  description = 'Update the connection for this tool';
  inputSchema = z.object({
    connectionData: z.object({
      type: z.enum(['apiKey', 'oauth', 'password', 'noAuth']),
      provider: z.string(),
      displayName: z.string(),
      secret: z.any(),
    }),
    updateScope: z.enum(['single', 'all']),
  });
  group = 'Tool';
  icon = 'key';

  constructor(
    private readonly client: TenantedClient['tools'],
    private readonly modalManager: {
      push: (props: {
        view: () => React.ReactNode;
        onClose?: () => void;
      }) => void;
      close: () => void;
    },
    private readonly queryClient: QueryClient,
    private readonly orgId: string
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    const toolModel = context.targetedModelOfType(ToolModel);

    if (toolModel === null) {
      return false;
    }

    return toolModel.currentNameInGraph.snapshot() !== null;
  }

  async execute(
    input: z.infer<typeof this.inputSchema>,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const tool = context.targetedModelOfType(ToolModel);
    const graph = context.getInScopeModelOfType(GraphModel);

    Assert.assert(tool !== null, 'No targeted tool');
    Assert.assert(graph !== null, 'Graph not found');

    const currentToolSymbol = tool.currentNameInGraph.snapshot();
    Assert.assert(currentToolSymbol !== null, 'Current tool symbol not found');

    // Get the current tool node from the graph
    const currentToolNode = graph.graph.snapshot().nodeMap[currentToolSymbol];
    Assert.assert(
      currentToolNode !== undefined && currentToolNode !== null,
      'Tool node not found'
    );
    Assert.assert(currentToolNode.t === 'tool', 'Node is not a tool');
    Assert.assert(currentToolNode.def.t === 'ffi', 'Tool is not an FFI tool');

    const currentToolId = currentToolNode.def.toolId;
    const currentConnectionId = currentToolNode.def.toolConnectionId;

    // Find all tools in the graph using the same connection
    const graphSnapshot = graph.graph.snapshot();
    const toolsUsingSameConnection = Object.entries(graphSnapshot.nodeMap)
      .filter(([_, node]) => {
        return (
          node.t === 'tool' &&
          node.def.t === 'ffi' &&
          node.def.toolConnectionId === currentConnectionId
        );
      })
      .map(([symbol, node]) => ({
        symbol,
        toolId:
          node.t === 'tool' && node.def.t === 'ffi' ? node.def.toolId : '',
        type: node.t === 'tool' && node.def.t === 'ffi' ? node.def.type : '',
      }));

    // Call the unified endpoint to update tools
    const updateRes = await this.client.updateToolsConnection({
      mode: input.updateScope,
      toolId: input.updateScope === 'single' ? currentToolId : undefined,
      connectionId: currentConnectionId,
      connectionData: {
        type: input.connectionData.type,
        provider: input.connectionData.provider,
        displayName: input.connectionData.displayName,
        secret: input.connectionData.secret!,
      },
    });

    if (updateRes.isErr()) {
      toast.error(
        `Failed to update connection: ${updateRes.error.err.errorType}`
      );
      return err([
        {
          t: updateRes.error.err.errorType,
          message:
            'message' in updateRes.error.err
              ? (updateRes.error.err.message as string)
              : undefined,
        },
      ]);
    }

    const { updatedTools, newConnectionId, count } = updateRes.value;

    // Invalidate and refetch queries
    await Promise.all([
      this.queryClient.invalidateQueries({
        queryKey: queryKeys.tools.all({ orgId: this.orgId }),
      }),
      this.queryClient.invalidateQueries({
        queryKey: queryKeys.connections.all({ orgId: this.orgId }),
      }),
    ]);

    await Promise.all([
      this.queryClient.refetchQueries({
        queryKey: queryKeys.tools.all({ orgId: this.orgId }),
      }),
      this.queryClient.refetchQueries({
        queryKey: queryKeys.connections.all({ orgId: this.orgId }),
      }),
    ]);

    // Create a map of old toolId to new tool for efficient lookup
    const toolIdMap = new Map(updatedTools.map((t) => [t.id, t]));

    // Create update operations for the graph
    const symbolsToUpdate =
      input.updateScope === 'all'
        ? toolsUsingSameConnection.map((t) => t.symbol)
        : [currentToolSymbol];

    const operations: DG.OperationData[] = [];

    for (const symbol of symbolsToUpdate) {
      const toolInfo = toolsUsingSameConnection.find(
        (t) => t.symbol === symbol
      );
      if (!toolInfo) {
        continue;
      }

      const updatedTool = toolIdMap.get(toolInfo.toolId);
      if (!updatedTool) {
        continue;
      }

      operations.push(
        DG.Operations.updateTool({
          symbol,
          def: {
            t: 'ffi',
            type: toolInfo.type,
            toolId: updatedTool.id,
            toolConnectionId: newConnectionId,
          },
        })
      );
    }

    const stagingResult = await graph.attemptToStageOperations(operations);

    if (stagingResult.isErr()) {
      const errorMessage =
        'message' in stagingResult.error.err
          ? (stagingResult.error.err.message as string)
          : JSON.stringify(stagingResult.error.err);
      toast.error(`Failed to update connection: ${stagingResult.error.err.t}`);
      return err([
        {
          t: stagingResult.error.err.t as string,
          message: errorMessage,
        },
      ]);
    }

    toast.success(
      count === 1
        ? 'Connection updated successfully'
        : `Updated connection for ${count} tools`
    );

    return ok({});
  }

  shouldAsk(context: ApplicableContext): boolean {
    const tool = context.targetedModelOfType(ToolModel);
    const graph = context.getInScopeModelOfType(GraphModel);

    if (tool === null || graph === null) {
      return false;
    }

    const currentToolSymbol = tool.currentNameInGraph.snapshot();
    if (currentToolSymbol === null) {
      return false;
    }

    // Get the current tool node from the graph
    const currentToolNode = graph.graph.snapshot().nodeMap[currentToolSymbol];
    if (
      currentToolNode === undefined ||
      currentToolNode === null ||
      currentToolNode.t !== 'tool' ||
      currentToolNode.def.t !== 'ffi'
    ) {
      return false;
    }

    const currentConnectionId = currentToolNode.def.toolConnectionId;

    // Find all tools in the graph using the same connection
    const graphSnapshot = graph.graph.snapshot();
    const toolsUsingSameConnection = Object.entries(
      graphSnapshot.nodeMap
    ).filter(([_, node]) => {
      return (
        node.t === 'tool' &&
        node.def.t === 'ffi' &&
        node.def.toolConnectionId === currentConnectionId
      );
    });

    // Ask if there are other tools using the same connection
    return toolsUsingSameConnection.length > 1;
  }

  ask(params: Partial<Params>, context: ExecuteContext): AskResponse<Params> {
    const toolModel = context.targetedModelOfType(ToolModel);
    const graph = context.getInScopeModelOfType(GraphModel);

    Assert.assert(toolModel !== null);
    Assert.assert(graph !== null);

    const currentToolSymbol = toolModel.currentNameInGraph.snapshot();
    Assert.assert(currentToolSymbol !== null);

    // Get the current tool node from the graph
    const currentToolNode = graph.graph.snapshot().nodeMap[currentToolSymbol];
    Assert.assert(currentToolNode !== undefined && currentToolNode !== null);
    Assert.assert(currentToolNode.t === 'tool');
    Assert.assert(currentToolNode.def.t === 'ffi');

    const currentConnectionId = currentToolNode.def.toolConnectionId;

    // Find all tools in the graph using the same connection
    const graphSnapshot = graph.graph.snapshot();
    const toolsUsingSameConnection = Object.entries(graphSnapshot.nodeMap)
      .filter(([_, node]) => {
        return (
          node.t === 'tool' &&
          node.def.t === 'ffi' &&
          node.def.toolConnectionId === currentConnectionId
        );
      })
      .map(([symbol]) => symbol);

    const otherToolSymbols = toolsUsingSameConnection.filter(
      (symbol) => symbol !== currentToolSymbol
    );

    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => {
            return (
              <AskView
                onRespond={async (askParams) => {
                  await onRespond({
                    ...params,
                    ...askParams,
                    connectionData: params.connectionData!,
                  } as Params);
                  this.modalManager.close();
                }}
                currentToolName={currentToolSymbol}
                otherToolSymbols={otherToolSymbols}
              />
            );
          },
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }
}

const AskView: React.FC<{
  onRespond: (params: { updateScope: 'single' | 'all' }) => Promise<void>;
  currentToolName: string;
  otherToolSymbols: string[];
}> = ({ onRespond, currentToolName, otherToolSymbols }) => {
  const [updateScope, setUpdateScope] = useState<'single' | 'all'>('all');
  const [loading, setLoading] = useState(false);

  return (
    <ModalPageView
      title="Update Connection"
      subtitle="Choose which tools to update"
      className="px-4 py-4"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
        }}
      >
        <RadioGroup
          value={updateScope}
          onValueChange={(v: string) => setUpdateScope(v as 'single' | 'all')}
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="single" id="single" />
            <Label htmlFor="single">Update only "{currentToolName}"</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="all" id="all" />
            <Label htmlFor="all">
              Update all other ({otherToolSymbols.length + 1}) tools using this
              connection
            </Label>
          </div>
        </RadioGroup>

        {updateScope === 'all' && otherToolSymbols.length > 0 && (
          <div className="text-sm text-muted-foreground p-2 bg-muted/50 rounded-md">
            <div className="font-medium mb-1">
              Other tools that will be updated:
            </div>
            <div>{otherToolSymbols.join(', ')}</div>
          </div>
        )}

        <div className="w-full flex justify-end gap-2">
          <Button
            type="submit"
            disabled={loading}
            variant="default"
            className="w-fit self-end"
            onClick={async () => {
              setLoading(true);
              await onRespond({ updateScope });
              setLoading(false);
            }}
          >
            {loading ? <Loading.Spinner variant="sm" /> : 'Update'}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
